import * as React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { toast } from '@/template/lib/toast';
import { ClipboardList, CheckCircle2, XCircle, Loader2, Euro, Users, Phone, FileText, AlertTriangle, BellRing } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { ActivityReportReviewCardProps } from '@/types/provider-components';
import { formatCurrency } from '@/lib/formatters';
import { formatBerlinDate, formatBerlinTime } from '@/lib/berlin-time';
import { getServerMessage } from '@/lib/convex-errors';
import { requiredSettlementDocuments } from '@/lib/settlement-documents';

/**
 * ActivityReportReviewCard - Provider view for reviewing substitute's activity report
 *
 * Features:
 * - 2x2 grid displaying activity counts
 * - Notfallscheinbeteiligung calculation preview
 * - Total deployments summary
 * - Confirm all button
 * - Reject with reason button
 * - Follows OKLCH color system
 */
// Mirrors the server's one-reminder-per-24h limit in remindSubstituteAboutDocuments
const REMINDER_INTERVAL_MS = 24 * 60 * 60 * 1000;

export const ActivityReportReviewCard: React.FC<ActivityReportReviewCardProps> = ({ dutyId, activityReport, hasNotfallscheinbeteiligung, notfallscheinbeteiligungRate, isConfirmed, auditOnly = false, uploadedDocuments, documentsReminderSentAt, onConfirmSuccess, onRejectSuccess, className }) => {
  const { t } = useTranslation(['duties']);
  const [showRejectDialog, setShowRejectDialog] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [isRejecting, setIsRejecting] = React.useState(false);

  const confirmServiceCompletion = useMutation(api.app.duties.mutations.confirmServiceCompletion as any);
  const confirmActivityReport = useMutation(api.app.duties.mutations.confirmActivityReport as any);
  const rejectActivityReport = useMutation(api.app.duties.mutations.rejectActivityReport as any);
  const remindSubstitute = useMutation(api.app.duties.mutations.remindSubstituteAboutDocuments);
  const [isReminding, setIsReminding] = React.useState(false);
  const reminderStatusId = React.useId();
  const reminderRecentlySent = documentsReminderSentAt !== undefined && Date.now() - documentsReminderSentAt < REMINDER_INTERVAL_MS;

  const handleRemind = async () => {
    setIsReminding(true);
    try {
      await remindSubstitute({ dutyId });
      toast.success(t('duties:activityReport.reminderSent'));
    } catch (err) {
      toast.error(getServerMessage(err, t('duties:activityReport.reminderFailed')));
    } finally {
      setIsReminding(false);
    }
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await (auditOnly ? confirmActivityReport({ dutyId }) : confirmServiceCompletion({ dutyId }));
      toast.success(t(auditOnly ? 'duties:activityReport.auditConfirmSuccess' : 'duties:activityReport.confirmSuccess'));
      onConfirmSuccess?.();
    } catch (err) {
      console.error('Failed to confirm service completion:', err);
      toast.error(getServerMessage(err, 'Fehler beim Bestätigen'));
    } finally {
      setIsConfirming(false);
    }
  };

  const handleReject = async () => {
    if (rejectionReason.length < 10) {
      toast.error(t('duties:activityReport.rejectReasonRequired'));
      return;
    }

    setIsRejecting(true);
    try {
      await rejectActivityReport({ dutyId, reason: rejectionReason });
      toast.success(t('duties:activityReport.rejectSuccess'));
      setShowRejectDialog(false);
      setRejectionReason('');
      onRejectSuccess?.();
    } catch (err: any) {
      console.error('Failed to reject activity report:', err);
      toast.error(err?.message || 'Fehler beim Ablehnen');
    } finally {
      setIsRejecting(false);
    }
  };

  // Format timestamp
  const formattedDate = formatBerlinDate(activityReport.submittedAt);
  const formattedTime = formatBerlinTime(activityReport.submittedAt);

  // Calculate totals
  const totalDeployments = activityReport.kassenpatienten + activityReport.privatpatienten + activityReport.telefonischeBeratungen + activityReport.totenscheine;

  const requiredDocuments = requiredSettlementDocuments(hasNotfallscheinbeteiligung, activityReport.kassenpatienten);
  const missingNoteId = React.useId();
  const missingDocuments = !auditOnly && uploadedDocuments !== undefined && uploadedDocuments < requiredDocuments;

  const scheinbeteiligungTotal = hasNotfallscheinbeteiligung && notfallscheinbeteiligungRate ? activityReport.kassenpatienten * notfallscheinbeteiligungRate : 0;

  const statItems = [
    {
      label: t('duties:activityReport.kassenpatienten'),
      value: activityReport.kassenpatienten,
      icon: Users,
    },
    {
      label: t('duties:activityReport.privatpatienten'),
      value: activityReport.privatpatienten,
      icon: Users,
    },
    {
      label: t('duties:activityReport.telefonischeBeratungen'),
      value: activityReport.telefonischeBeratungen,
      icon: Phone,
    },
    {
      label: t('duties:activityReport.totenscheine'),
      value: activityReport.totenscheine,
      icon: FileText,
    },
  ];

  return (
    <>
      <Card className={cn('overflow-hidden shadow-sm hover:shadow-md transition-all duration-200', className)}>
        {/* Header */}
        <div className='p-4 lg:p-6 pb-0'>
          <div className='flex items-start gap-3'>
            <div className='flex items-center justify-center size-10 rounded-full bg-primary/10 shrink-0'>
              <ClipboardList className='size-5 text-primary' />
            </div>
            <div className='flex-1 min-w-0'>
              <h3 className='text-base font-semibold text-foreground'>{t('duties:activityReport.title')}</h3>
              <p className='text-sm text-muted-foreground'>{t('duties:activityReport.submittedAt', { date: `${formattedDate} um ${formattedTime}` })}</p>
              {auditOnly && <p className='text-xs text-info'>{t('duties:activityReport.auditDescription')}</p>}
            </div>
            {isConfirmed && (
              <div className='flex items-center gap-1.5 text-success'>
                <CheckCircle2 className='size-4' />
                <span className='text-sm font-medium'>{t('duties:detail.confirmed')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className='p-4 lg:p-6 space-y-4'>
          {/* 2x2 Stats Grid */}
          <div className='grid grid-cols-2 gap-3'>
            {statItems.map((item) => (
              <div key={item.label} className='rounded-lg bg-muted/30 p-4 text-center'>
                <div className='text-3xl font-bold text-foreground mb-1'>{item.value}</div>
                <div className='text-sm text-muted-foreground flex items-center justify-center gap-1.5'>
                  <item.icon className='size-3.5' />
                  {item.label}
                </div>
              </div>
            ))}
          </div>

          {/* Total Row */}
          <div className='border-t border-border pt-4'>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-medium text-foreground'>{t('duties:activityReport.total')}</span>
              <span className='text-sm text-muted-foreground'>{t('duties:activityReport.totalDeployments', { count: totalDeployments })}</span>
            </div>
          </div>

          {/* Notfallscheinbeteiligung Preview */}
          {hasNotfallscheinbeteiligung && notfallscheinbeteiligungRate && (
            <div className='rounded-lg bg-primary/5 border border-primary/20 p-4'>
              <div className='flex items-start gap-3'>
                <div className='flex items-center justify-center size-8 rounded-full bg-primary/10 shrink-0'>
                  <Euro className='size-4 text-primary' />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='text-sm font-medium text-foreground mb-0.5'>Notfallscheinbeteiligung</div>
                  <div className='text-sm text-muted-foreground'>
                    {activityReport.kassenpatienten} Patienten x {formatCurrency(notfallscheinbeteiligungRate, 'de', 'EUR', { maximumFractionDigits: 0 })} = <span className='font-semibold text-primary'>{formatCurrency(scheinbeteiligungTotal, 'de', 'EUR', { maximumFractionDigits: 0 })}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {missingDocuments && (
            <div className='flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4' data-testid='missing-documents-note'>
              <AlertTriangle className='mt-0.5 size-5 shrink-0 text-warning' aria-hidden='true' />
              <div className='space-y-1'>
                <div id={missingNoteId} role='status' className='space-y-1'>
                  <div className='text-sm font-medium text-foreground'>
                    {hasNotfallscheinbeteiligung ? t('duties:activityReport.documentsMissingNfb', { uploaded: uploadedDocuments, required: requiredDocuments }) : t('duties:activityReport.documentsMissing')}
                  </div>
                  <div className='text-sm text-muted-foreground'>{t(hasNotfallscheinbeteiligung ? 'duties:activityReport.documentsMissingDescNfb' : 'duties:activityReport.documentsMissingDesc')}</div>
                </div>
                <div className='flex flex-wrap items-center gap-3 pt-2'>
                  <Button variant='outline' size='sm' onClick={handleRemind} disabled={isReminding || reminderRecentlySent} aria-describedby={documentsReminderSentAt !== undefined ? reminderStatusId : undefined} className='min-h-11'>
                    {isReminding ? <Loader2 className='mr-2 size-4 animate-spin' aria-hidden='true' /> : <BellRing className='mr-2 size-4' aria-hidden='true' />}
                    {t('duties:activityReport.remindSubstitute')}
                  </Button>
                  {documentsReminderSentAt !== undefined && (
                    <span id={reminderStatusId} className='text-sm text-muted-foreground'>
                      {t('duties:activityReport.reminderSentAt', { date: formatBerlinDate(documentsReminderSentAt), time: formatBerlinTime(documentsReminderSentAt) })}
                      {reminderRecentlySent && ` ${t('duties:activityReport.reminderAvailableAt', { date: formatBerlinDate(documentsReminderSentAt + REMINDER_INTERVAL_MS), time: formatBerlinTime(documentsReminderSentAt + REMINDER_INTERVAL_MS) })}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!isConfirmed && (
            <div className='flex items-center gap-3 pt-2'>
              {!auditOnly && (
                <Button variant='outline' onClick={() => setShowRejectDialog(true)} disabled={isConfirming || isRejecting} className='min-h-11 flex-1'>
                  <XCircle className='size-4 mr-2' />
                  {t('duties:activityReport.reject')}
                </Button>
              )}
              <Button onClick={handleConfirm} disabled={isConfirming || isRejecting || missingDocuments} aria-describedby={missingDocuments ? missingNoteId : undefined} className='min-h-11 flex-1'>
                {isConfirming ? <Loader2 className='size-4 mr-2 animate-spin' /> : <CheckCircle2 className='size-4 mr-2' />}
                {auditOnly ? t('duties:activityReport.auditConfirm') : t('duties:activityReport.confirmAll')}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('duties:activityReport.reject')}</DialogTitle>
            <DialogDescription>{t('duties:activityReport.rejectDescription')}</DialogDescription>
          </DialogHeader>
          <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder={t('duties:activityReport.rejectionReasonPlaceholder')} className='min-h-[100px]' />
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowRejectDialog(false)} disabled={isRejecting}>
              {t('duties:activityReport.cancel')}
            </Button>
            <Button variant='destructive' onClick={handleReject} disabled={isRejecting || rejectionReason.length < 10}>
              {isRejecting ? <Loader2 className='size-4 mr-2 animate-spin' /> : null}
              {t('duties:activityReport.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
